'use client'

// Modern biotech "DNA code rain" — vertical columns of A/T/C/G letters
// scrolling downward at slightly varied speeds. A small fraction of the
// letters glow lime; the rest sit muted in olive. Pure CSS animation,
// no per-frame JS. Designed to slot into a card on the hero. The shape
// is tall and contained — doesn't bleed off-edge anymore.

import { useMemo } from 'react'

const LETTERS = 'ATCG'
const COLS = 7
const ROWS = 32
// Per-column animation duration (s) — slight variation feels alive
const SPEEDS = [18, 22, 16, 24, 19, 26, 20]

// Deterministic PRNG — keeps SSR/CSR markup identical
function mulberry32(seed: number) {
  return () => {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

interface ColData { letters: string[]; lit: boolean[]; brightness: number[] }

function buildColumns(): ColData[] {
  const rand = mulberry32(1729)
  const cols: ColData[] = []
  for (let c = 0; c < COLS; c++) {
    const letters: string[] = []
    const lit: boolean[] = []
    const brightness: number[] = []
    for (let r = 0; r < ROWS * 2; r++) {
      letters.push(LETTERS[Math.floor(rand() * 4)])
      lit.push(rand() < 0.16)
      brightness.push(0.25 + rand() * 0.55)
    }
    cols.push({ letters, lit, brightness })
  }
  return cols
}

interface Props {
  className?: string
  style?: React.CSSProperties
}

export default function HeroHelix({ className, style }: Props) {
  const columns = useMemo(buildColumns, [])

  return (
    <div
      aria-hidden="true"
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none',
        ...style,
      }}
    >
      <style>{`
        @keyframes hh-rain {
          from { transform: translateY(0); }
          to   { transform: translateY(-50%); }
        }
        .hh-rain-col {
          position: absolute;
          top: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, Menlo, monospace;
          font-size: 13px;
          line-height: 1.55;
          font-weight: 600;
          letter-spacing: 0.08em;
          will-change: transform;
          animation-name: hh-rain;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .hh-rain-glyph { display: block; }
        .hh-rain-glyph[data-lit="1"] {
          color: #9FAF08;
          text-shadow: 0 0 6px rgba(159,175,8,0.7);
        }

        /* Soft fade at top + bottom — letters dissolve into the card */
        .hh-rain-mask {
          position: absolute; inset: 0;
          background: linear-gradient(
            to bottom,
            #F5EED9 0%,
            transparent 18%,
            transparent 82%,
            #F5EED9 100%
          );
          pointer-events: none;
          z-index: 2;
        }

        /* A single subtle vertical accent line behind the letters */
        .hh-rain-axis {
          position: absolute;
          top: 12%; bottom: 12%;
          left: 50%;
          width: 1px;
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(159,175,8,0.35) 30%,
            rgba(159,175,8,0.35) 70%,
            transparent 100%
          );
          z-index: 0;
        }

        /* Corner ticks — modern card detail */
        .hh-rain-tick {
          position: absolute; width: 8px; height: 8px;
          border: 1px solid rgba(91,112,5,0.55);
          z-index: 3;
        }
        .hh-rain-tick.tl { top: 6px;  left: 6px;  border-right: none; border-bottom: none; }
        .hh-rain-tick.tr { top: 6px;  right: 6px; border-left:  none; border-bottom: none; }
        .hh-rain-tick.bl { bottom: 6px; left: 6px;  border-right: none; border-top: none; }
        .hh-rain-tick.br { bottom: 6px; right: 6px; border-left:  none; border-top: none; }

        @media (prefers-reduced-motion: reduce) {
          .hh-rain-col { animation: none !important; }
        }
      `}</style>

      <span className="hh-rain-axis" />

      {columns.map((col, ci) => {
        const leftPct = ((ci + 0.5) / COLS) * 100
        const duration = SPEEDS[ci % SPEEDS.length]
        const delay = -(ci * 1.7)
        return (
          <div
            key={ci}
            className="hh-rain-col"
            style={{
              left: `${leftPct}%`,
              transform: 'translateX(-50%)',
              animationDuration: `${duration}s`,
              animationDelay: `${delay}s`,
            }}
          >
            {col.letters.map((ltr, ri) => (
              <span
                key={ri}
                className="hh-rain-glyph"
                data-lit={col.lit[ri] ? '1' : '0'}
                style={{
                  color: col.lit[ri] ? undefined : `rgba(91,112,5,${col.brightness[ri].toFixed(2)})`,
                }}
              >
                {ltr}
              </span>
            ))}
          </div>
        )
      })}

      <span className="hh-rain-mask" />
      <span className="hh-rain-tick tl" />
      <span className="hh-rain-tick tr" />
      <span className="hh-rain-tick bl" />
      <span className="hh-rain-tick br" />
    </div>
  )
}
